import { getPlayerOptions, getPlayCoverInfo, sortByObjectProperty, isPresentationId } from '../util';
import { Path } from './config.js';
import { appendAuthTicket } from '../util';

// Values for d.Presentation.PlayStatus
/**
* @enum {string}
*/
export const PresentationStatus = Object.freeze({
    NotAvailable: "NotAvailable",
    ScheduledForLive: "ScheduledForLive",
    OpenForLive: "OpenForLive",
    Live: "Live",
    LivePaused: "LivePaused",
    LiveEnded: "LiveEnded",
    LiveEndedDvr: "LiveEndedDvr",
    OnDemand: "OnDemand",
});

// Values for d.PlayerPresentationStatus
/**
* @enum {number}
*/
export const PlayerPresentationStatus = Object.freeze({
    None: 0,
    Viewable: 1,
    NotFound: 2,
    Offline: 3,
    Scheduled: 4,
    NotYetAvailable: 5,
    NotAuthorized: 6
});

// Values for d.Presentation.TimedEvents
/**
* @enum {string}
*/
export const TimedEventType = Object.freeze({
    Chapter: 'application/vnd.sonicfoundry.chapter.xml',
    Poll: 'mediasite/poll',
    PresentationSubQuiz: 'mediasite/presentationSubQuiz',
    Annotation: 'SfPresentationAnnotation'
});

/**
 * Values for SonicFoundry.Mediasite.Player.DataAccess.TimelineEventType
 * @enum {number}
 **/
export const LiveTimelineEventType = Object.freeze({
    Unknown: 0,
    Slide: 1,
    PresentationStart: 2,
    PresentationEnd: 3,
    PauseStart: 4,
    PauseEnd: 5,
    TimedEvent: 6
});

/**
 * @enum {number}
 */
export const ContentStreamType = Object.freeze({
    Video1: 0,
    Slide: 2,
    Presentation: 3,
    Video2: 4,
    Video3: 5
});

/**
 * @enum {number}
 */
export const SearchResultType = Object.freeze({
    Caption: 1,
    Slide: 2
});

export const MediaErrorType = Object.freeze({
    CustomError: 0,
    AbortedError: 1,
    NetworkError: 2,
    DecodeError: 3,
    SrcNotSupported: 4,
    Encrypted: 5
});

/**
 * @typedef {Object} PresentationMetadata
 * @property {string} title
 * @property {string} poster
 * @property {string} playerPresentationStatusMessage
 * @property {number} playerPresentationStatus
 * @property {number} duration
 * @property {string} playStatus
 * @property {number} unixTime
 * @property {Array} presenters
 * @property {Array} supportingLinks
 * @property {string} description
 * @property {string} compliancePolicyMarkdown
 * @property {number} bookmark
 * @property {boolean} is360Video
 * @property {boolean} isLiveCountdownEnabled
 * @property {boolean} isLiveIndicationDelayed
 */

/**
 * 
 * @param {string} presentationId
 * @returns {Promise<PresentationMetadata>}
 */
export function getMetadata(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        return {
            title: presentationOptions.d.Presentation?.Title,
            poster: appendAuthTicket(presentationId, presentationOptions.d.Presentation?.ThumbnailUrl),
            playerPresentationStatusMessage: presentationOptions.d.PlayerPresentationStatusMessage,
            playerPresentationStatus: presentationOptions.d.PlayerPresentationStatus,
            duration: presentationOptions.d.Presentation?.Duration,
            serverClockSkew: presentationOptions.d.Presentation?.ServerClockSkew ?? 0,
            playStatus: presentationOptions.d.Presentation?.PlayStatus,
            unixTime: presentationOptions.d.Presentation?.UnixTime,
            presenters: presentationOptions.d.Presentation?.Presenters?.map(presenter => {
                return {
                    name: JSON.parse(`"${presenter.Name}"`),
                    bioUrl: JSON.parse(`"${presenter.BioUrl}"`),
                    imageUrl: presenter.ImageUrl || presentationOptions.d.PlayerLayoutOptions?.Images?.DefaultPresenterImage?.ImageFilename
                };
            }) ?? [],
            description: presentationOptions.d.Presentation?.Description,
            supportingLinks: presentationOptions.d.Presentation?.SupportingLinks?.map?.(link => {
                return {
                    title: JSON.parse(`"${link.Description}"`),
                    url: JSON.parse(`"${link.Url}"`)
                }
            }) ?? [],
            compliancePolicyMarkdown: presentationOptions.d.GlobalOptions?.CompliancePolicyMarkdown,
            bookmark: presentationOptions.d.PresentationBookmark?.position,
            is360Video: presentationOptions.d.Presentation?.Is360Video,
            isLiveCountdownEnabled: presentationOptions.d.Presentation?.LiveCountdownEnabled,
            isLiveIndicationDelayed: presentationOptions.d.Presentation?.LiveIndicationDelayed,
        }
    });
}

/**
 * @typedef {Object} VideoSource
 * @property {string} src
 * @property {string} type
 * @property {number} segmentLength
 * @property {number} streamType
 */

function isHlsMime(type) {
    return type === 'audio/x-mpegurl';
}

function prioritizeHls(a, b) {
    if (isHlsMime(a.type) && !isHlsMime(b.type)) {
        return -1;
    }
    else if (isHlsMime(b.type) && !isHlsMime(a.type)) {
        return 1;
    }
    else {
        return 0;
    }
}

/**
 * @typedef {Object} MediaStream
 * @property {string} streamType
 * @property {VideoSource[]} items
 * @property {boolean} playbackAudioSource
 * @property {number} priority
 * @property {string} [slideBaseUrl]
 */

/**
 * 
 * @param {any} presentationId
 * @returns {Promise<MediaStream[]>}
 */
export function getStreams(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        const streams = presentationOptions.d.Presentation?.Streams
            ?.filter(stream => (stream.VideoUrls.length > 0 || stream.HasSlideContent))
            .slice()
            .sort((a, b) => sortByObjectProperty(a, b, 'Priority')) ?? [];

        return streams.map(stream => {
            switch (stream.StreamType) {
                case ContentStreamType.Video1:
                case ContentStreamType.Video2:
                case ContentStreamType.Video3: {
                    const videoUrls = stream.VideoUrls.map(videoUrl => {
                        const locationUrl = new URL(videoUrl.Location);
                        locationUrl.protocol = window.location.protocol;
                        return {
                            src: locationUrl.href,
                            type: videoUrl.MimeType,
                            segmentLength: videoUrl.SegmentLength,
                            streamType: stream.StreamType
                        }
                    }) ?? [];
                    videoUrls.sort(prioritizeHls);
                    return {
                        id: stream.Id,
                        audioOnly: stream.AudioOnly,
                        streamType: stream.StreamType,
                        items: videoUrls,
                        playbackAudioSource: stream.PlaybackAudioSource,
                        priority: stream.Priority
                    };
                }
                case ContentStreamType.Slide:
                    const slides = stream.Slides.map(slide => {
                        return {
                            number: slide.Number,
                            text: slide.Text,
                            time: slide.Time,
                            streamType: ContentStreamType.Slide
                        }
                    });
                    return {
                        id: stream.Id,
                        audioOnly: stream.AudioOnly,
                        streamType: stream.StreamType,
                        items: slides,
                        slideBaseUrl: stream.SlideBaseUrl,
                        playbackAudioSource: stream.PlaybackAudioSource,
                        priority: stream.Priority
                    }
                default:
                    break;
            }
        });
    });
}
/**
 * 
 * @param {string} presentationId
 * @returns {Promise<string>}
 */
export function getAudioDescriptionUrl(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => (
        appendAuthTicket(presentationId, presentationOptions.d.Presentation?.AudioDescriptionsUrl)
    ));
}

/**
 * @typedef {Object} DownloadInfo
 * @property {string} text
 * @property {string} url
 */

/**
 * 
 * @param {string} presentationId
 * @returns {Promise<Array<DownloadInfo>>}
 */
export function getDownloads(presentationId) {
    return isPresentationId(presentationId) ? getPlayerOptions(presentationId).then(presentationOptions => {
        const result = [];
        if (presentationOptions.d.Presentation?.PodcastUrl) {
            result.push({
                text: 'Audio (Podcast)',
                url: appendAuthTicket(presentationId, presentationOptions.d.Presentation?.PodcastUrl)
            });
        }
        if (presentationOptions.d.Presentation?.VodcastUrl) {
            result.push({
                text: 'Composite (Vodcast)',
                url: appendAuthTicket(presentationId, presentationOptions.d.Presentation?.VodcastUrl)
            });
        }
        return result;
    }) : Promise.resolve([{ text: 'Example', url: location.href }]);
}

/**
 * 
 * @param {string} presentationId
 * @returns {Promise<VTTCue[]>}
 */
export function getCaptions(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        const captions = presentationOptions.d.Presentation?.Transcript ?? [];
        return captions.map(caption => {
            //Parsing to JSON handles decoding any encoding done by server-side
            const captionText = JSON.parse(`"${caption.Text}"`);
            return new VTTCue(caption.Time / 1000, caption.EndTime / 1000, captionText);
        })
    });
}

/**
 * 
 * @param {string} presentationId
 * @returns {Promise<TimedEvent[]>}
 */
export function getChapters(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        return (presentationOptions.d.Presentation?.Chapters ?? []).map(chapter => {
            //Parsing to JSON handles decoding any encoding done by server-side
            const chapterText = JSON.parse(`"${chapter.Text}"`);
            return Object.assign({}, chapter, { Text: chapterText });
        });
    });
}

/**
 * @typedef {Object} PresentationOptions
 * @property {boolean} HasSearchableText
 * @property {boolean} ForumEnabled
 * @property {boolean} SharingEnabled
 * @property {boolean} PollingEnabled
 * @property {boolean} HasQuizzes
 */

/**
 * 
 * @param {string} presentationId
 * @returns {Promise<PresentationOptions>}
 */

export function getPresentationOptions(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        return {
            HasSearchableText: presentationOptions.d.Presentation?.HasSearchableText,
            ForumEnabled: presentationOptions.d.Presentation?.ForumEnabled,
            SharingEnabled: presentationOptions.d.Presentation?.SharingEnabled,
            PollingEnabled: presentationOptions.d.Presentation?.PollingEnabled,
            HasQuizzes: presentationOptions.d.Presentation?.HasQuizzes,
        }
    });
}

/**
 * @param {string} presentationId
 * @returns {Promise<{completedDurationInMs:number,liveStartUnixTimeInMs:number,playStatus:string}>}
 */
export function getLiveStatus(presentationId) {
    return fetch(`${Path.PlayerService}/GetLiveStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({ presentationId })
    }).then(result => result.json()).then(jsonData => ({
        completedDurationInMs: jsonData.d.CompletedDurationInMs,
        liveStartUnixTimeInMs: jsonData.d.LiveStartUnixTimeInMs,
        playStatus: jsonData.d.PlayStatus
    }));
}

/**
 * @typedef {Object} TimedEvent
 * @property {string} Id
 * @property {string} Text
 * @property {number} Time
 * @property {string} Type
 * @property {string} _type
 */

/**
 * 
 * @param {string} presentationId
 * @returns {Promise<TimedEvent[]>}
 */
export function getTimedEvents(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        return presentationOptions.d.Presentation?.TimedEvents ?? [];
    });
}

/**
 * @typedef {Object} CompositionMap
 * @property {number} Width
 * @property {number} Height
 * @property {number} Duration
 * @property {Array<VisualComposition>} VisualCompositions
 */

/**
 * @typedef {Object} VisualComposition
 * @property {number} Time
 * @property {Array<VisualCompositionItem>} VisualItems
 */

/**
 * @typedef {Object} VisualCompositionItem
 * @property {string} SourceId
 * @property {Rect} TargetRect
 */

/**
 * @typedef {Object} Rect
 * @property {number} X
 * @property {number} Y
 * @property {number} Width
 * @property {number} Height
 */

/**
 *
 * @param {string} presentationId
 * @returns {Promise<CompositionMap[]>}
 */
export function getCompositionMap(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        return presentationOptions.d.Presentation?.CompositionMap;
    });
}

/**
 * @typedef {Object} PosterInfo
 * @property {string} title
 * @property {string} poster
 * @property {string} playStatus
 * @property {number} unixTime
 * @property {boolean} hasQuizzes
 * @property {string[]} allowedPlayerIFrameAPISites
 */

/**
 * 
 * @param {string} presentationId
 * @returns {Promise<PosterInfo>}
 */
export function getPosterInfo(presentationId, coverVisible) {
    const playcoverNullObjectPromise = new Promise((resolve, reject) => {
        const playcoverNullObject = {
            title: null,
            poster: null,
            playStatus: null,
            unixTime: null,
            hasQuizzes: null,
            allowedPlayerIFrameAPISites: null,
            enableLiveCountdown: null,
            delayLiveIndicationUntilPresentationDate: null
        }
        resolve(playcoverNullObject)
    })

    if (!isPresentationId(presentationId) || coverVisible === false) {
        return playcoverNullObjectPromise
    }
    else {
        return getPlayCoverInfo(presentationId).then(playCoverOptions => {
            return {
                title: playCoverOptions.d.Title,
                poster: appendAuthTicket(presentationId, playCoverOptions.d?.ThumbnailUrl) ?? playCoverOptions.d.DefaultThumbnailUrl,
                playStatus: playCoverOptions.d.PlayStatus,
                unixTime: playCoverOptions.d.AirDateUnixTimeInMs,
                hasQuizzes: playCoverOptions.d.HasQuizzes,
                allowedPlayerIFrameAPISites: playCoverOptions.d.AllowedPlayerIFrameAPISites,
                enableLiveCountdown: playCoverOptions.d.IsLiveCountdownEnabled,
                delayLiveIndicationUntilPresentationDate: playCoverOptions.d.IsLiveIndicationDelayed
            }
        });
    }
}

export function sendQuestion(playbackTicketId, question) {
    return fetch(`${Path.PlayerService}/SendQuestion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({ playbackTicketId, question })
    }).then(result => result.json()).then(jsonData => {
        const { Message, Success } = jsonData.d;
        // Success variable indicates whether question was successfully submited (after server side validation, etc.)
        return { Success, Message };
    });
}

export function getShareEmail(playbackTicketId) {
    return fetch(`${Path.PlayerService}/GetInvite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({ playbackTicketId })
    }).then(result => result.json()).then(jsonData => {
        return {
            text: jsonData.d.MessageBody,
            title: jsonData.d.Subject,
            url: jsonData.d.MailTo
        }
    });
}


/**
 * @typedef {Object} PresentationTextSearchResult
 * @property {string} text The text excerpt where the result was found.
 * @property {number} time The time in milliseconds.
 * @property {SearchResultType} type The type of result.
 */

/**
 * 
 * @param {string} playbackTicketId
 * @param {string} searchTerm
 * @returns {Promise<PresentationTextSearchResult[]>}
 */
export function searchCaptionsAndSlides(playbackTicketId, searchTerm) {
    return fetch(`${Path.PlayerService}/SearchPresentationText`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({ playbackTicketId, searchTerm })
    }).then(result => result.json()).then(jsonData => {
        return jsonData.d.SearchResults?.map(searchResult => ({
            text: searchResult.Text,
            time: searchResult.Time,
            type: searchResult.Type
        })) ?? [];
    });
}
